import { Heading } from "@chakra-ui/layout";
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Select,
  useToast,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Layout } from "components/atoms";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FieldErrors, useForm } from "react-hook-form";
import formatter from "utils/formatter";
import {
  FormRegistrationElementsValues,
  FormRegistrationProps,
} from "./interfaces";
import { createSchema } from "./schema";

const defaultValues: FormRegistrationElementsValues = {
  ufTaxa: "",
  descricao: "",
  cnpjAgenteFinanceiro: "",
  publica: "true",
  ativa: "true",
  valorRegistradora: "R$ 0,00",
  valorTaxaRegistro: "R$ 0,00",
  valorTaxaAditivo: "R$ 0,00",
  dataVigenciaInicio: "",
  dataVigenciaFim: "",
  tipoVeiculo: "TODOS",
};

const ufOptions = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO",
];

const FormRegistration: React.FC<FormRegistrationProps> = ({
  onSubmitForm,
  onCancelForm,
  itemEdit,
  mode,
}) => {
  const toast = useToast();
  const [cnpjAgenteFinanceiro, setCnpjAgenteFinanceiro] = useState("");
  const [publica, setPublica] = useState("true");
  const [valorRegistradora, setValorRegistradora] = useState("R$ 0,00");
  const [valorTaxaRegistro, setValorTaxaRegistro] = useState("R$ 0,00");
  const [valorTaxaAditivo, setValorTaxaAditivo] = useState("R$ 0,00");
  const submittingRef = useRef(false);

  const validationSchema = useMemo(() => createSchema(mode), [mode]);

  const {
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormRegistrationElementsValues>({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
    defaultValues,
  });

  const isEditing = !!(itemEdit?.id || itemEdit?.guid);
  const ufSelecionada = watch("ufTaxa");
  const ufComTipoVeiculo = ufSelecionada === "AP" || ufSelecionada === "CE";

  const formatDateToInput = (value?: string): string => {
    if (!value) return "";

    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.substring(0, 10);
    }

    return formatter.formatDateToEn(value);
  };

  const formatCnpj = (value?: string | null): string => {
    return formatter
      .onlyNumber(value ?? "")
      .slice(0, 14)
      .replace(/^(\d{2})(\d)/, "$1.$2")
      .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d)/, ".$1/$2")
      .replace(/(\d{4})(\d)/, "$1-$2");
  };

  const formatMoneyInput = (value?: string | number | null): string => {
    const onlyNumbers = String(value ?? "").replace(/\D/g, "");
    const numberValue = Number(onlyNumbers || 0) / 100;

    return formatter.moeda(numberValue) || "R$ 0,00";
  };

  const formatMoneyFromApi = (value?: number | null): string => {
    return formatter.moeda(value ?? 0) || "R$ 0,00";
  };

  const updateFieldValue = (
    field: keyof FormRegistrationElementsValues,
    value: string,
  ) => {
    setValue(field, value, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  useEffect(() => {
    if (!isEditing || !itemEdit) {
      setCnpjAgenteFinanceiro("");
      setPublica("true");
      setValorRegistradora("R$ 0,00");
      setValorTaxaRegistro("R$ 0,00");
      setValorTaxaAditivo("R$ 0,00");
      reset(defaultValues);
      return;
    }

    const cnpjEdit = formatCnpj(itemEdit.cnpjAgenteFinanceiro);
    const publicaEdit = String(itemEdit.publica ?? true);

    const valorRegistradoraEdit = formatMoneyFromApi(
      itemEdit.valorRegistradora,
    );
    const valorTaxaRegistroEdit = formatMoneyFromApi(
      itemEdit.valorTaxaRegistro,
    );
    const valorTaxaAditivoEdit = formatMoneyFromApi(itemEdit.valorTaxaAditivo);

    setCnpjAgenteFinanceiro(cnpjEdit);
    setPublica(publicaEdit);
    setValorRegistradora(valorRegistradoraEdit);
    setValorTaxaRegistro(valorTaxaRegistroEdit);
    setValorTaxaAditivo(valorTaxaAditivoEdit);

    reset({
      ufTaxa: itemEdit.ufTaxa ?? "",
      descricao: itemEdit.descricao ?? "",
      cnpjAgenteFinanceiro: cnpjEdit,
      publica: publicaEdit,
      ativa: String(itemEdit.ativa),
      valorRegistradora: valorRegistradoraEdit,
      valorTaxaRegistro: valorTaxaRegistroEdit,
      valorTaxaAditivo: valorTaxaAditivoEdit,
      dataVigenciaInicio: formatDateToInput(itemEdit.dataVigenciaInicio),
      dataVigenciaFim: formatDateToInput(itemEdit.dataVigenciaFim),
      tipoVeiculo: itemEdit.tipoVeiculo ?? "TODOS",
    });
  }, [itemEdit, isEditing, mode, reset]);

  useEffect(() => {
    if (isEditing) return;
    setValue("tipoVeiculo", ufComTipoVeiculo ? "DEMAIS_VEICULOS" : "TODOS", {
      shouldValidate: true,
    });
  }, [isEditing, setValue, ufComTipoVeiculo]);

  const handleChangeCnpjAgente = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = formatCnpj(event.target.value);

    setCnpjAgenteFinanceiro(value);
    updateFieldValue("cnpjAgenteFinanceiro", value);
  };

  const handleChangePublica = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;

    setPublica(value);
    updateFieldValue("publica", value);
  };

  const handleChangeValorRegistradora = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = formatMoneyInput(event.target.value);

    setValorRegistradora(value);
    updateFieldValue("valorRegistradora", value);
  };

  const handleChangeValorTaxaRegistro = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = formatMoneyInput(event.target.value);

    setValorTaxaRegistro(value);
    updateFieldValue("valorTaxaRegistro", value);
  };

  const handleChangeValorTaxaAditivo = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = formatMoneyInput(event.target.value);

    setValorTaxaAditivo(value);
    updateFieldValue("valorTaxaAditivo", value);
  };

  const onSubmit = async (
    value: FormRegistrationElementsValues,
  ): Promise<void> => {
    if (submittingRef.current) return;

    submittingRef.current = true;

    try {
      await onSubmitForm({
        ...value,
        cnpjAgenteFinanceiro,
        publica,
        valorRegistradora,
        valorTaxaRegistro,
        valorTaxaAditivo,
      });
    } finally {
      submittingRef.current = false;
    }
  };

  const onInvalid = (
    formErrors: FieldErrors<FormRegistrationElementsValues>,
  ): void => {
    const firstError = Object.keys(formErrors)
      .map(
        (field) =>
          formErrors[field as keyof FormRegistrationElementsValues],
      )
      .find((error) => typeof error?.message === "string");

    toast({
      title: "Não foi possível salvar",
      description:
        typeof firstError?.message === "string"
          ? firstError.message
          : "Verifique os campos obrigatórios.",
      position: "bottom-left",
      status: "warning",
      isClosable: true,
    });
  };

  return (
    <Box overflowY="auto" maxH="calc(100vh - 120px)">
      <form onSubmit={handleSubmit(onSubmit, onInvalid)} data-testid="form">
        <Layout
          type="container"
          full
          px={{ base: 4, md: 8 }}
          py={4}
          display="grid"
          gap={6}
        >
          <Layout type="row">
            <Heading size="md" textAlign={{ sm: "center" }}>
              {`${
                isEditing ? "Editar" : "Adicionar"
              } Tabela de ${mode === "price" ? "Preço" : "Taxa"}`}
            </Heading>
          </Layout>

          <Layout type="row" gutter={{ y: 1.5, x: 1.5 }}>
            <Layout type="col" layout={{ base: 12, md: 2 }}>
              <FormControl isRequired isInvalid={!!errors.ufTaxa}>
                  <FormLabel>UF</FormLabel>

                  {isEditing ? (
                      <>
                          {/*
                          * O Select desabilitado é apenas visual.
                          * O input hidden mantém a UF registrada no formulário.
                          */}
                          <input
                              type="hidden"
                              {...register('ufTaxa')}
                          />

                          <Select
                              value={ufSelecionada}
                              isDisabled
                          >
                              {ufOptions.map(uf => (
                                  <option key={uf} value={uf}>
                                      {uf}
                                  </option>
                              ))}
                          </Select>
                      </>
                  ) : (
                      <Select
                          placeholder="Clique para selecionar"
                          {...register('ufTaxa')}
                      >
                          {ufOptions.map(uf => (
                              <option key={uf} value={uf}>
                                  {uf}
                              </option>
                          ))}
                      </Select>
                  )}

                  <FormErrorMessage>
                      {errors.ufTaxa?.message}
                  </FormErrorMessage>
              </FormControl>
          </Layout>

            <Layout type="col" layout={{ base: 12, md: 5 }}>
              <FormControl isRequired isInvalid={!!errors.descricao}>
                <FormLabel>Descrição</FormLabel>

                <Input placeholder="Digite aqui" {...register("descricao")} />

                <FormErrorMessage>{errors.descricao?.message}</FormErrorMessage>
              </FormControl>
            </Layout>

            <Layout type="col" layout={{ base: 12, md: 2 }}>
              <FormControl isRequired isInvalid={!!errors.tipoVeiculo}>
                <FormLabel>Tipo Veículo</FormLabel>
                <Select {...register("tipoVeiculo")}>
                  {!ufComTipoVeiculo && <option value="TODOS">Todos</option>}
                  {ufComTipoVeiculo && (
                    <>
                      <option value="DEMAIS_VEICULOS">Demais veículos</option>
                      <option value="MOTO">Moto</option>
                    </>
                  )}
                </Select>
                <FormErrorMessage>
                  {errors.tipoVeiculo?.message}
                </FormErrorMessage>
              </FormControl>
            </Layout>

            <Layout type="col" layout={{ base: 12, md: 3 }}>
              <FormControl isRequired isInvalid={!!errors.ativa}>
                <FormLabel>Status</FormLabel>

                <Select {...register("ativa")}>
                  <option value="true">Ativo</option>
                  <option value="false">Inativo</option>
                </Select>

                <FormErrorMessage>{errors.ativa?.message}</FormErrorMessage>
              </FormControl>
            </Layout>
          </Layout>

          {mode === "price" && (
            <Box>
              <Divider borderColor="gray.400" mb={3} />

              <Heading as="h3" size="sm" bg="gray.50" px={3} py={2} mb={4}>
                Tabela de Preço
              </Heading>

              <Layout type="row" gutter={{ y: 1.5, x: 1.5 }}>
                <Layout type="col" layout={{ base: 12, md: 4 }}>
                  <FormControl isInvalid={!!errors.cnpjAgenteFinanceiro}>
                    <FormLabel>CNPJ Agente Financeiro</FormLabel>

                    <Input
                      placeholder="Vazio = taxa padrão"
                      maxLength={18}
                      inputMode="numeric"
                      value={cnpjAgenteFinanceiro}
                      onChange={handleChangeCnpjAgente}
                    />

                    <FormErrorMessage>
                      {errors.cnpjAgenteFinanceiro?.message}
                    </FormErrorMessage>
                  </FormControl>
                </Layout>

                <Layout type="col" layout={{ base: 12, md: 4 }}>
                  <FormControl isRequired isInvalid={!!errors.publica}>
                    <FormLabel>Preço</FormLabel>

                    <Select value={publica} onChange={handleChangePublica}>
                      <option value="false">Livre concorrência</option>
                      <option value="true">Preço Público Detran</option>
                    </Select>

                    <FormErrorMessage>
                      {errors.publica?.message}
                    </FormErrorMessage>
                  </FormControl>
                </Layout>

                <Layout type="col" layout={{ base: 12, md: 4 }}>
                  <FormControl
                    isRequired
                    isInvalid={!!errors.valorRegistradora}
                  >
                    <FormLabel>Valor Registradora</FormLabel>

                    <Input
                      placeholder="R$ 0,00"
                      inputMode="numeric"
                      maxLength={15}
                      value={valorRegistradora}
                      onChange={handleChangeValorRegistradora}
                    />

                    <FormErrorMessage>
                      {errors.valorRegistradora?.message}
                    </FormErrorMessage>
                  </FormControl>
                </Layout>
              </Layout>
            </Box>
          )}

          {mode === "tax" && (
            <Box>
              <Divider borderColor="gray.400" mb={3} />

              <Heading as="h3" size="sm" bg="gray.50" px={3} py={2} mb={4}>
                Taxa Detran
              </Heading>

              <Layout type="row" gutter={{ y: 1.5, x: 1.5 }}>
                <Layout type="col" layout={{ base: 12, md: 3 }}>
                  <FormControl
                    isRequired
                    isInvalid={!!errors.valorTaxaRegistro}
                  >
                    <FormLabel>Valor Taxa Registro</FormLabel>

                    <Input
                      placeholder="R$ 0,00"
                      inputMode="numeric"
                      maxLength={15}
                      value={valorTaxaRegistro}
                      onChange={handleChangeValorTaxaRegistro}
                    />

                    <FormErrorMessage>
                      {errors.valorTaxaRegistro?.message}
                    </FormErrorMessage>
                  </FormControl>
                </Layout>

                <Layout type="col" layout={{ base: 12, md: 3 }}>
                  <FormControl isRequired isInvalid={!!errors.valorTaxaAditivo}>
                    <FormLabel>Valor Taxa Registro Aditivo</FormLabel>

                    <Input
                      placeholder="R$ 0,00"
                      inputMode="numeric"
                      maxLength={15}
                      value={valorTaxaAditivo}
                      onChange={handleChangeValorTaxaAditivo}
                    />

                    <FormErrorMessage>
                      {errors.valorTaxaAditivo?.message}
                    </FormErrorMessage>
                  </FormControl>
                </Layout>

                <Layout type="col" layout={{ base: 12, md: 3 }}>
                  <FormControl
                    isRequired
                    isInvalid={!!errors.dataVigenciaInicio}
                  >
                    <FormLabel>Data Início de Vigência</FormLabel>

                    <Input
                      type="date"
                      max="9999-12-31"
                      {...register("dataVigenciaInicio")}
                    />

                    <FormErrorMessage>
                      {errors.dataVigenciaInicio?.message}
                    </FormErrorMessage>
                  </FormControl>
                </Layout>

                <Layout type="col" layout={{ base: 12, md: 3 }}>
                  <FormControl isRequired isInvalid={!!errors.dataVigenciaFim}>
                    <FormLabel>Data Fim de Vigência</FormLabel>

                    <Input
                      type="date"
                      max="9999-12-31"
                      {...register("dataVigenciaFim")}
                    />

                    <FormErrorMessage>
                      {errors.dataVigenciaFim?.message}
                    </FormErrorMessage>
                  </FormControl>
                </Layout>
              </Layout>
            </Box>
          )}

          {mode === "price" && (
            <Layout type="row" gutter={{ y: 1.5, x: 1.5 }}>
              <Layout type="col" layout={{ base: 12, md: 6 }}>
                <FormControl isRequired isInvalid={!!errors.dataVigenciaInicio}>
                  <FormLabel>Data Início de Vigência</FormLabel>
                  <Input
                    type="date"
                    max="9999-12-31"
                    {...register("dataVigenciaInicio")}
                  />
                  <FormErrorMessage>
                    {errors.dataVigenciaInicio?.message}
                  </FormErrorMessage>
                </FormControl>
              </Layout>
              <Layout type="col" layout={{ base: 12, md: 6 }}>
                <FormControl isRequired isInvalid={!!errors.dataVigenciaFim}>
                  <FormLabel>Data Fim de Vigência</FormLabel>
                  <Input
                    type="date"
                    max="9999-12-31"
                    {...register("dataVigenciaFim")}
                  />
                  <FormErrorMessage>
                    {errors.dataVigenciaFim?.message}
                  </FormErrorMessage>
                </FormControl>
              </Layout>
            </Layout>
          )}

          <Layout type="row">
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap={6}
            >
              <Button
                colorScheme="primary"
                variant="outline"
                onClick={onCancelForm}
              >
                Cancelar
              </Button>

              <Button
                colorScheme="primary"
                type="submit"
                isLoading={isSubmitting}
                isDisabled={isSubmitting}
              >
                Salvar
              </Button>
            </Box>
          </Layout>
        </Layout>
      </form>
    </Box>
  );
};

export default FormRegistration;
